import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../../lib/supabase';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { 
  generateCourseSlug,
  type ElearningCategory,
  type ElearningInstructor,
  type ElearningCourse
} from '../../../services/elearningCourseService';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  Upload, 
  X,
  Plus,
  Loader2,
  BookOpen,
  Image
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Separator } from '../../../components/ui/separator';
import { MediaSelector } from '../../../components/media/MediaSelector';
import { ElearningCategorySelect } from '../../../components/admin/elearning/ElearningCategorySelect';

interface CourseFormData {
  title: string;
  slug: string;
  description: string;
  content: string;
  price: number;
  currency: string;
  thumbnail_url: string;
  video_preview_url: string;
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  duration_hours: number;
  language: string;
  status: 'draft' | 'published' | 'archived';
  category_id: string;
  instructor_id: string;
  tags: string[];
  requirements: string[];
  learning_objectives: string[];
  is_featured: boolean;
}

const CourseFormPage: React.FC = () => {
  useCheckPermissions(['admin']);
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>(); // Extraction explicite du paramètre id
  const courseId = params.id;
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const isEditing = Boolean(courseId && courseId !== 'new');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [categories, setCategories] = useState<ElearningCategory[]>([]);
  const [instructors, setInstructors] = useState<ElearningInstructor[]>([]);
  
  const [formData, setFormData] = useState<CourseFormData>({
    title: '',
    slug: '',
    description: '',
    content: '',
    price: 0,
    currency: 'FCFA',
    thumbnail_url: '',
    video_preview_url: '',
    difficulty_level: 'beginner',
    duration_hours: 1,
    language: 'fr',
    status: 'draft',
    category_id: '',
    instructor_id: '',
    tags: [],
    requirements: [],
    learning_objectives: [],
    is_featured: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newTag, setNewTag] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newObjective, setNewObjective] = useState('');
  const [showThumbnailSelector, setShowThumbnailSelector] = useState(false);
  const [showVideoSelector, setShowVideoSelector] = useState(false);

  const uploadFileAndGetUrl = async (file: File, folderName: string): Promise<string> => {
    const { tenantId } = getDomainFilterParams();
    const fileExt = file.name.split('.').pop();
    const fileName = `${folderName}_${uuidv4()}.${fileExt}`;
    const filePath = `tenant/${tenantId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data: publicUrlData } = supabase.storage
      .from('media')
      .getPublicUrl(filePath);

    return publicUrlData.publicUrl;
  };

  useEffect(() => {
    console.log('CourseFormPage - courseId:', courseId);
    console.log('CourseFormPage - isEditing:', isEditing);
    loadInitialData();
  }, [tenant?.id, courseId]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      // Charger les catégories avec filtrage domaine
      let categoriesQuery = supabase
        .from('elearn_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      // Appliquer le filtre domaine
      categoriesQuery = applyDomainFilterToQuery(categoriesQuery);
      
      const { data: categoriesData, error: categoriesError } = await categoriesQuery;

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // Charger les instructeurs avec filtrage domaine
      let instructorsQuery = supabase
        .from('elearn_instructors')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });
      
      // Appliquer le filtre domaine
      instructorsQuery = applyDomainFilterToQuery(instructorsQuery);
      
      const { data: instructorsData, error: instructorsError } = await instructorsQuery;

      if (instructorsError) throw instructorsError;
      setInstructors(instructorsData || []);

      // Si on édite un cours, le charger avec filtrage domaine
      if (isEditing && courseId) {
        console.log('CourseFormPage - Chargement du cours:', courseId);
        
        let courseQuery = supabase
          .from('elearn_courses')
          .select(`
            *,
            category:category_id(*),
            instructor:instructor_id(*)
          `)
          .eq('id', courseId);
        
        // Appliquer le filtre domaine
        courseQuery = applyDomainFilterToQuery(courseQuery);
        
        const { data: course, error: courseError } = await courseQuery.single();

        console.log('CourseFormPage - Résultat requête:', { course, courseError });

        if (courseError) throw courseError;
        
        if (course) {
          console.log('CourseFormPage - Données du cours trouvées:', course);
          setFormData({
            title: course.title,
            slug: course.slug,
            description: course.description || '',
            content: course.content || '',
            price: course.price,
            currency: course.currency,
            thumbnail_url: course.thumbnail_url || '',
            video_preview_url: course.video_preview_url || '',
            difficulty_level: course.difficulty_level,
            duration_hours: course.duration_hours,
            language: course.language,
            status: course.status,
            category_id: course.category_id || '',
            instructor_id: course.instructor_id || '',
            tags: course.tags || [],
            requirements: course.requirements || [],
            learning_objectives: course.learning_objectives || [],
            is_featured: course.is_featured
          });
          console.log('CourseFormPage - FormData mis à jour');
        }
      }
    } catch (error) {
      console.error('CourseFormPage - Erreur lors du chargement des données:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données nécessaires',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof CourseFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Générer automatiquement le slug à partir du titre
    if (field === 'title' && !isEditing) {
      const slug = generateCourseSlug(value);
      setFormData(prev => ({ ...prev, slug }));
    }
    
    // Supprimer l'erreur si le champ est maintenant valide
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addRequirement = () => {
    if (newRequirement.trim() && !formData.requirements.includes(newRequirement.trim())) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (reqToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter(req => req !== reqToRemove)
    }));
  };

  const addObjective = () => {
    if (newObjective.trim() && !formData.learning_objectives.includes(newObjective.trim())) {
      setFormData(prev => ({
        ...prev,
        learning_objectives: [...prev.learning_objectives, newObjective.trim()]
      }));
      setNewObjective('');
    }
  };

  const removeObjective = (objToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      learning_objectives: prev.learning_objectives.filter(obj => obj !== objToRemove)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Le slug est requis';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }

    if (formData.price < 0) {
      newErrors.price = 'Le prix ne peut pas être négatif';
    }

    if (formData.duration_hours <= 0) {
      newErrors.duration_hours = 'La durée doit être supérieure à 0';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'La catégorie est requise';
    }

    if (!formData.instructor_id) {
      newErrors.instructor_id = 'L\'instructeur est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return null;
    
    setIsSaving(true);
    try {
      // Générer un slug si nécessaire
      let slug = formData.slug.trim();
      if (!slug) {
        slug = generateCourseSlug(formData.title);
        setFormData(prev => ({ ...prev, slug }));
      }
      
      // Récupérer les paramètres de domaine
      const { domain, tenantId } = getDomainFilterParams();
      
      const courseData = {
        ...formData,
        tenant_id: parseInt(tenantId),
        domain: domain || undefined,
        slug,
        updated_at: new Date().toISOString()
      };
      
      let savedCourseId: string | null = null;
      
      if (isEditing && courseId) {
        // Mise à jour d'un cours existant
        let updateQuery = supabase
          .from('elearn_courses')
          .update(courseData)
          .eq('id', courseId);
        
        // Appliquer le filtre domaine
        updateQuery = applyDomainFilterToQuery(updateQuery);
        
        const { error } = await updateQuery;
          
        if (error) throw error;
        savedCourseId = courseId;
      } else {
        // Création d'un nouveau cours
        const newCourseId = uuidv4();
        const { error } = await supabase
          .from('elearn_courses')
          .insert({
            ...courseData,
            id: newCourseId,
            created_at: new Date().toISOString(),
            enrollment_count: 0,
            rating_average: 0,
            rating_count: 0,
            sort_order: 1
          });
          
        if (error) throw error;
        savedCourseId = newCourseId;
      }
      
      toast({
        title: 'Succès',
        description: isEditing ? 'Cours mis à jour avec succès' : 'Cours créé avec succès',
      });
      
      // Si cette fonction est appelée directement (pas via handleSaveAndContinue)
      // alors on redirige vers la liste des cours
      if (e.type === 'submit') {
        navigate('/admin/elearning/courses');
      }
      
      return savedCourseId;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du cours:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder le cours',
        variant: 'destructive'
      });
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndContinue = async () => {
    if (!validateForm()) return;
    
    // Sauvegarder puis rediriger vers le builder
    const savedCourseId = await handleSubmit(new Event('submit') as any);
    // Si nous avons un ID de cours (soit existant, soit nouvellement créé), naviguer vers le builder
    if (savedCourseId) {
      navigate(`/admin/elearning/courses/builder/${savedCourseId}`);
    } else if (courseId) {
      // Fallback sur l'ID existant si handleSubmit ne retourne pas d'ID
      navigate(`/admin/elearning/courses/builder/${courseId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin h-8 w-8 text-red-500" />
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto py-8 px-4 space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white">
            {isEditing ? 'Modifier le cours' : 'Créer un nouveau cours'}
          </h1>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/elearning/courses/${formData.slug}`)}
              disabled={!formData.slug}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Eye className="mr-2" size={16} />
              Prévisualiser
            </Button>
            <Button
              type="button"
              onClick={handleSaveAndContinue}
              disabled={isSaving}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {isSaving ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <Save className="mr-2 text-white" size={16} />
              )}
              Sauvegarder et continuer
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informations de base */}
          <Card className="bg-black/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Informations de base</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-white">Titre du cours *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="bg-black/30 border-white/20 text-white"
                  placeholder="Ex: Introduction au développement web"
                />
                {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title}</p>}
              </div>

              <div>
                <Label htmlFor="slug" className="text-white">Slug (URL) *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  className="bg-black/30 border-white/20 text-white"
                  placeholder="introduction-developpement-web"
                />
                {errors.slug && <p className="text-red-400 text-sm mt-1">{errors.slug}</p>}
              </div>

              <div>
                <Label htmlFor="description" className="text-white">Description courte *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="bg-black/30 border-white/20 text-white"
                  placeholder="Description qui apparaîtra dans les listes de cours"
                  rows={3}
                />
                {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
              </div>

              <div>
                <Label htmlFor="content" className="text-white">Description complète</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  className="bg-black/30 border-white/20 text-white"
                  placeholder="Description détaillée du cours, ce que les étudiants vont apprendre..."
                  rows={6}
                />
              </div>
            </CardContent>
          </Card>

          {/* Médias */}
          <Card className="bg-black/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Médias</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Image de couverture */}
              <div>
                <Label htmlFor="thumbnail_url" className="text-white mb-2 block">Image de couverture</Label>
                <div className="space-y-3">
                  <Input
                    id="thumbnail_url"
                    value={formData.thumbnail_url}
                    onChange={(e) => handleInputChange('thumbnail_url', e.target.value)}
                    className="bg-black/30 border-white/20 text-white"
                    placeholder="https://example.com/image.jpg"
                  />
                  
                  {formData.thumbnail_url && (
                    <div className="relative w-full h-48 bg-white/10 rounded-lg overflow-hidden">
                      <img
                        src={formData.thumbnail_url}
                        alt="Aperçu de l'image de couverture"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => handleInputChange('thumbnail_url', '')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowThumbnailSelector(true)}
                      className="border-white/10 text-white hover:bg-white/5"
                    >
                      <Image className="mr-2 h-4 w-4" />
                      Médiathèque
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            setIsSaving(true); // Activer l'indicateur de sauvegarde pendant l'upload
                            try {
                              const url = await uploadFileAndGetUrl(file, 'course_thumbnails');
                              handleInputChange('thumbnail_url', url);
                              toast({
                                title: 'Image de couverture uploadée',
                                description: 'L\'image a été uploadée avec succès.',
                              });
                            } catch (error) {
                              console.error('Erreur lors de l\'upload de l\'image:', error);
                              toast({
                                title: 'Erreur d\'upload',
                                description: 'Impossible d\'uploader l\'image de couverture.',
                                variant: 'destructive',
                              });
                            } finally {
                              setIsSaving(false);
                            }
                          }
                        };
                        input.click();
                      }}
                      className="border-white/10 text-white hover:bg-white/5"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Uploader
                    </Button>
                  </div>
                </div>
              </div>

              {/* Vidéo de présentation */}
              <div>
                <Label htmlFor="video_preview_url" className="text-white mb-2 block">Vidéo de présentation</Label>
                <div className="space-y-3">
                  <Input
                    id="video_preview_url"
                    value={formData.video_preview_url}
                    onChange={(e) => handleInputChange('video_preview_url', e.target.value)}
                    className="bg-black/30 border-white/20 text-white"
                    placeholder="https://example.com/video.mp4"
                  />
                  
                  {formData.video_preview_url && (
                    <div className="relative w-full h-48 bg-white/10 rounded-lg overflow-hidden">
                      <video
                        src={formData.video_preview_url}
                        className="w-full h-full object-cover"
                        controls
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => handleInputChange('video_preview_url', '')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowVideoSelector(true)}
                      className="border-white/10 text-white hover:bg-white/5"
                    >
                      <Image className="mr-2 h-4 w-4" />
                      Médiathèque
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'video/*';
                        input.onchange = async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            setIsSaving(true); // Activer l'indicateur de sauvegarde pendant l'upload
                            try {
                              const url = await uploadFileAndGetUrl(file, 'course_videos');
                              handleInputChange('video_preview_url', url);
                              toast({
                                title: 'Vidéo de présentation uploadée',
                                description: 'La vidéo a été uploadée avec succès.',
                              });
                            } catch (error) {
                              console.error('Erreur lors de l\'upload de la vidéo:', error);
                              toast({
                                title: 'Erreur d\'upload',
                                description: 'Impossible d\'uploader la vidéo de présentation.',
                                variant: 'destructive',
                              });
                            } finally {
                              setIsSaving(false);
                            }
                          }
                        };
                        input.click();
                      }}
                      className="border-white/10 text-white hover:bg-white/5"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Uploader
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card className="bg-black/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="bg-black/30 border-white/20 text-white"
                  placeholder="Ajouter un tag"
                />
                <Button type="button" onClick={addTag} variant="outline" className="border-white/20">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="bg-red-500/20 text-red-300">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 hover:text-red-100"
                    >
                      <X size={12} />
                    </button>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Prérequis */}
          <Card className="bg-black/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Prérequis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <Input
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                  className="bg-black/30 border-white/20 text-white"
                  placeholder="Ajouter un prérequis"
                />
                <Button type="button" onClick={addRequirement} variant="outline" className="border-white/20">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="space-y-2">
                {formData.requirements.map((req, index) => (
                  <div key={index} className="flex items-center justify-between bg-white/5 p-2 rounded">
                    <span className="text-white">{req}</span>
                    <button
                      type="button"
                      onClick={() => removeRequirement(req)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Objectifs d'apprentissage */}
          <Card className="bg-black/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Objectifs d'apprentissage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <Input
                  value={newObjective}
                  onChange={(e) => setNewObjective(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addObjective())}
                  className="bg-black/30 border-white/20 text-white"
                  placeholder="Ajouter un objectif"
                />
                <Button type="button" onClick={addObjective} variant="outline" className="border-white/20">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="space-y-2">
                {formData.learning_objectives.map((obj, index) => (
                  <div key={index} className="flex items-center justify-between bg-white/5 p-2 rounded">
                    <span className="text-white">{obj}</span>
                    <button
                      type="button"
                      onClick={() => removeObjective(obj)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Paramètres */}
          <Card className="bg-black/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Paramètres</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="status" className="text-white">Statut</Label>
                <Select value={formData.status} onValueChange={(value: any) => handleInputChange('status', value)}>
                  <SelectTrigger className="bg-black/30 border-white/20 text-white">
                    {formData.status === 'draft' && <SelectValue>Brouillon</SelectValue>}
                    {formData.status === 'published' && <SelectValue>Publié</SelectValue>}
                    {formData.status === 'archived' && <SelectValue>Archivé</SelectValue>}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="published">Publié</SelectItem>
                    <SelectItem value="archived">Archivé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="category_id" className="text-white">Catégorie *</Label>
                <ElearningCategorySelect
                  categories={categories}
                  selectedCategoryId={formData.category_id}
                  onChange={(categoryId) => handleInputChange('category_id', categoryId)}
                  onCategoryCreated={(newCategory) => {
                    // Recharger les catégories après création
                    loadInitialData();
                  }}
                />
                {errors.category_id && <p className="text-red-400 text-sm mt-1">{errors.category_id}</p>}
              </div>

              <div>
                <Label htmlFor="instructor_id" className="text-white">Instructeur *</Label>
                <Select value={formData.instructor_id} onValueChange={(value) => handleInputChange('instructor_id', value)}>
                  <SelectTrigger className="bg-black/30 border-white/20 text-white">
                    {formData.instructor_id ? (
                      <SelectValue>
                        {instructors.find(inst => inst.id === formData.instructor_id)?.name || 'Instructeur inconnu'}
                      </SelectValue>
                    ) : (
                      <SelectValue placeholder="Sélectionner un instructeur" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map(instructor => (
                      <SelectItem key={instructor.id} value={instructor.id}>
                        {instructor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.instructor_id && <p className="text-red-400 text-sm mt-1">{errors.instructor_id}</p>}
              </div>

              <div>
                <Label htmlFor="difficulty_level" className="text-white">Niveau de difficulté</Label>
                <Select value={formData.difficulty_level} onValueChange={(value: any) => handleInputChange('difficulty_level', value)}>
                  <SelectTrigger className="bg-black/30 border-white/20 text-white">
                    {formData.difficulty_level === 'beginner' && <SelectValue>Débutant</SelectValue>}
                    {formData.difficulty_level === 'intermediate' && <SelectValue>Intermédiaire</SelectValue>}
                    {formData.difficulty_level === 'advanced' && <SelectValue>Avancé</SelectValue>}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Débutant</SelectItem>
                    <SelectItem value="intermediate">Intermédiaire</SelectItem>
                    <SelectItem value="advanced">Avancé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => handleInputChange('is_featured', checked)}
                />
                <Label htmlFor="is_featured" className="text-white">Cours en vedette</Label>
              </div>
            </CardContent>
          </Card>

          {/* Prix et durée */}
          <Card className="bg-black/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Prix et durée</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="price" className="text-white">Prix (FCFA)</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                  className="bg-black/30 border-white/20 text-white"
                />
                {errors.price && <p className="text-red-400 text-sm mt-1">{errors.price}</p>}
              </div>

              <div>
                <Label htmlFor="duration_hours" className="text-white">Durée (heures)</Label>
                <Input
                  id="duration_hours"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={formData.duration_hours}
                  onChange={(e) => handleInputChange('duration_hours', parseFloat(e.target.value) || 1)}
                  className="bg-black/30 border-white/20 text-white"
                />
                {errors.duration_hours && <p className="text-red-400 text-sm mt-1">{errors.duration_hours}</p>}
              </div>

              <div>
                <Label htmlFor="language" className="text-white">Langue</Label>
                <Select value={formData.language} onValueChange={(value) => handleInputChange('language', value)}>
                  <SelectTrigger className="bg-black/30 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="en">Anglais</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card className="bg-black/20 border-white/10">
            <CardContent className="p-4 space-y-3">
              <Button
                type="submit"
                disabled={isSaving}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 text-white" size={16} />
                    {isEditing ? 'Mettre à jour' : 'Créer le cours'}
                  </>
                )}
              </Button>
              
              {isEditing && (
                <Button
                  type="button"
                  onClick={() => navigate(`/admin/elearning/courses/builder/${courseId}`)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <BookOpen className="mr-2" size={16} />
                  Gérer les modules et leçons
                </Button>
              )}
              
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/elearning/courses')}
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Annuler
              </Button>
            </CardContent>
          </Card>
        </div>
      </form>
      
      {/* Sélecteurs de média */}
      <MediaSelector
        isOpen={showThumbnailSelector}
        onClose={() => setShowThumbnailSelector(false)}
        onSelect={(url) => {
          if (typeof url === 'string') {
            handleInputChange('thumbnail_url', url);
            setShowThumbnailSelector(false);
          }
        }}
        selectedUrl={formData.thumbnail_url || ''}
        title="Sélectionner une image de couverture"
        allowUpload={true}
      />
      
      <MediaSelector
        isOpen={showVideoSelector}
        onClose={() => setShowVideoSelector(false)}
        onSelect={(url) => {
          if (typeof url === 'string') {
            handleInputChange('video_preview_url', url);
            setShowVideoSelector(false);
          }
        }}
        selectedUrl={formData.video_preview_url || ''}
        title="Sélectionner une vidéo de présentation"
        allowUpload={true}
      />
    </div>
  </AdminLayout>
  );
};

export default CourseFormPage;
